import { useCallback, useEffect, useMemo, useState } from 'react'
import { selectCurrency, setAppCurrency } from '@/redux/app'
import { useAppDispatch, useAppSelector } from '@/redux/core'
import { CURRENCIES, Currencies, SSR_RENDERING_DELAY, usePackageInfo } from '@/utils'
import { useLazyUserLocationQuery } from '@/redux/lookup'
import { useIsSsr } from '@/core/ssrDetector'
import { currencies } from './CurrencyAndLocation.data'

export const useCurrencyAndLocationController = () => {
  const appCurrency = useAppSelector(selectCurrency)
  const dispatch = useAppDispatch()
  const { isInsidePackageFlow } = usePackageInfo()
  const isSsr = useIsSsr()

  const [currency, setCurrency] = useState<Currencies>(
    !isSsr && appCurrency.isFetched ? appCurrency.value : CURRENCIES.USD,
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [fetchUserLocationInfo] = useLazyUserLocationQuery()

  const onSelectCurrency = useCallback(
    (value: Currencies) => {
      const supportedValue = currencies.some((c) => c.value === value) ? value : CURRENCIES.USD
      setCurrency(supportedValue)
      dispatch(setAppCurrency({ value: supportedValue, isFetched: true }))
    },
    [dispatch],
  )

  const getUserLocation = useCallback(async () => {
    try {
      const {
        data: { data },
      } = await fetchUserLocationInfo({
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).unwrap()

      setTimeout(() => {
        onSelectCurrency(data.displayCurrency)
      }, SSR_RENDERING_DELAY)
    } catch {
      onSelectCurrency(CURRENCIES.USD)
    }
  }, [fetchUserLocationInfo, onSelectCurrency])

  const selectedCurrency = useMemo(
    () => currencies.find((c) => c.value === currency) ?? currencies[0],
    [currency],
  )

  useEffect(() => {
    if (!appCurrency.isFetched && !isInsidePackageFlow) getUserLocation()
  }, [appCurrency.isFetched, getUserLocation, isInsidePackageFlow])

  useEffect(() => {
    setTimeout(() => {
      onSelectCurrency(appCurrency.value)
    }, SSR_RENDERING_DELAY)
  }, [appCurrency.value, onSelectCurrency])

  return {
    isModalOpen,
    setIsModalOpen,
    selectedCurrency,
    onSelectCurrency,
  }
}
